import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions, sel } from '../store/studioStore';

/**
 * useSave
 * Gère la persistance locale (localStorage) + sauvegarde serveur debounced.
 *
 * @param {object} api       Instance createStudioApi — null si pas d'API configurée
 * @param {string} eventId   ID de l'événement pour scoper le localStorage
 */
export function useSave(api, eventId) {
  const dispatch = useDispatch();
  const doc = useSelector(sel.doc);
  const pages = useSelector(sel.pages);
  const showCropMarks = useSelector(sel.showCropMarks);
  const db = useSelector(sel.db);
  const documentId = useSelector(sel.documentId);
  const documentName = useSelector(sel.documentName);
  const saveTimerRef = useRef(null);

  // Use refs to always capture the latest state
  const stateRef = useRef();
  stateRef.current = {
    doc,
    pages,
    showCropMarks,
    db,
    documentId,
    documentName,
  };

  // ── localStorage key scoped by eventId ────────────────────────────────────
  const getLocalStorageKey = useCallback(() => {
    return eventId
      ? `studio-pro-v4-project-event-${eventId}`
      : 'studio-pro-v4-project';
  }, [eventId]);

  // ── localStorage ──────────────────────────────────────────────────────────
  const saveLocal = useCallback(() => {
    try {
      localStorage.setItem(
        getLocalStorageKey(),
        JSON.stringify({
          doc,
          pages,
          showCropMarks,
          db,
          documentId,
          documentName,
        })
      );
    } catch {
      // Quota exceeded — sauvegarde sans les blobs images
      try {
        const stripped = JSON.parse(
          JSON.stringify({
            doc,
            pages,
            showCropMarks,
            db,
            documentId,
            documentName,
          })
        );
        stripped.pages.forEach((p) => {
          if (p.background) p.background.src = null;
          p.elements.forEach((e) => {
            if (e.type === 'image') e.src = null;
          });
        });
        localStorage.setItem(getLocalStorageKey(), JSON.stringify(stripped));
      } catch (e2) {
        console.error('localStorage save failed', e2);
      }
    }
  }, [
    doc,
    pages,
    showCropMarks,
    db,
    documentId,
    documentName,
    getLocalStorageKey,
  ]);

  const loadLocal = useCallback(() => {
    const raw = localStorage.getItem(getLocalStorageKey());
    if (!raw) return false;
    try {
      const data = JSON.parse(raw);
      dispatch(studioActions.loadDocumentContent(data));
      dispatch(studioActions.pushHistory());
      return true;
    } catch {
      return false;
    }
  }, [dispatch, getLocalStorageKey]);

  // ── Server save ───────────────────────────────────────────────────────────
  const saveToServer = useCallback(async () => {
    if (!api) {
      console.warn('No API available, cannot save to server');
      dispatch(
        studioActions.setSaveStatus({
          status: 'error',
          error: 'No API connection',
        })
      );
      return;
    }

    dispatch(studioActions.setSaveStatus({ status: 'saving' }));

    // Use the latest state from ref
    const { doc, pages, showCropMarks, db, documentId, documentName } =
      stateRef.current;
    const content = { doc, pages, showCropMarks, db };

    try {
      let saved;
      if (documentId) {
        saved = await api.updateDocument(documentId, documentName, content);
      } else {
        // Use a default name if documentName is empty
        const name =
          documentName || `Document ${new Date().toISOString().split('T')[0]}`;
        saved = await api.createDocument(name, content);
        dispatch(
          studioActions.setDocumentMeta({
            id: saved.id,
            name: saved.name || name,
          })
        );
      }
      dispatch(studioActions.setSaveStatus({ status: 'saved' }));

      // Only save to localStorage as backup after successful server save
      saveLocal();
    } catch (err) {
      console.error('Server save failed:', err);
      dispatch(
        studioActions.setSaveStatus({ status: 'error', error: err.message })
      );
      // Save to localStorage as fallback when server fails
      saveLocal();
    }
  }, [api, dispatch, saveLocal]);

  // ── Debounced trigger ─────────────────────────────────────────────────────
  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    dispatch(studioActions.setSaveStatus({ status: 'saving' }));
    // Call saveToServer directly after timeout to use latest state
    saveTimerRef.current = setTimeout(() => {
      saveToServer();
    }, 800);
  }, [saveToServer, dispatch]);

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  return { saveLocal, loadLocal, scheduleSave, saveToServer };
}
